/**
 * A thin typed wrapper over `fetch` for the JSON API. It always sends cookies (same-origin) so the
 * Identity auth cookie flows automatically — the client needs no auth library or token storage.
 * Non-2xx responses are surfaced as {@link ApiError}, with ASP.NET Core validation problems parsed
 * into a field -> messages map.
 */
export interface ApiError {
  status: number;
  message: string;
  errors?: Record<string, string[]>;
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    'message' in value
  );
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `Request failed with status ${response.status}.`;
  let errors: Record<string, string[]> | undefined;

  try {
    const data = (await response.json()) as Record<string, unknown>;
    if (data && typeof data === 'object') {
      if (typeof data['error'] === 'string') {
        message = data['error'] as string;
      } else if (typeof data['title'] === 'string') {
        message = data['title'] as string;
      }
      // ASP.NET Core ValidationProblem shape: { errors: { field: [messages] } }.
      if (data['errors'] && typeof data['errors'] === 'object') {
        errors = data['errors'] as Record<string, string[]>;
        const first = Object.values(errors)[0];
        if (Array.isArray(first) && first.length > 0 && typeof first[0] === 'string') {
          message = first[0];
        }
      }
    }
  } catch {
    // Non-JSON error body; keep the default message.
  }

  return { status: response.status, message, errors };
}

async function request<T>(path: string, method: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw await parseError(response);
  }

  // 204 or empty body.
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path, 'GET'),
  post: <T>(path: string, body?: unknown) => request<T>(path, 'POST', body),
  put: <T>(path: string, body?: unknown) => request<T>(path, 'PUT', body),
  delete: <T>(path: string, body?: unknown) => request<T>(path, 'DELETE', body),
};
